import { Checkbox, Chip, ChipColorEnum, Column } from '@acceso/design-system';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import {
  ReliabilityInlineDropdown,
  SupportInlineDropdown,
  TimelineEndInlineControl,
  TimelineStartInlineControl,
  useBroadcastScheduleContext,
  VisibilityInlineDropdown,
} from '@/components/VideoPresencesInlineControls';
import { itemHasBeenValidated } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  FrameActionType,
  PostPresenceFor,
  TBroadcastScheduleOnUpdateInline,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { TPresenceItem } from '@/types/presences';

type VideoPresencesSubTableProps<T extends TPresenceItem> = {
  item: T & { children: T[] };
  columns: Column<T>[];
  onPreview?: (item: T, frame?: FrameActionType) => void;
  onUpdate?: (item: T) => void;
  onDelete?: (item: T, silently?: boolean) => void;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<PostPresenceFor<T>>;
  showSupportColumn?: boolean;
  renderEntityCell: (child: T) => ReactNode;
  entityIdKey: keyof T;
  buildDataToSave?: (item: T) => PostPresenceFor<T>;
  presenceData?: { id?: string };
  activeEntityId?: string;
  tableDataId?: string;
};

export function VideoPresencesSubTable<T extends TPresenceItem>({
  item,
  columns,
  onPreview,
  onUpdate,
  onDelete,
  onUpdateInline,
  showSupportColumn = false,
  renderEntityCell,
  entityIdKey,
  buildDataToSave,
  presenceData,
  activeEntityId,
  tableDataId = 'presences-subtable',
}: VideoPresencesSubTableProps<T>) {
  const { t } = useTranslation();
  const { setSelectedPresencesIds, selectedPresencesIds } = useBroadcastScheduleContext();

  const sortKey = entityIdKey as string;

  const onValidatingChild = (child: T) => {
    if (!buildDataToSave) return;
    const data = buildDataToSave(child) as any;
    data.review_state = 1;
    onUpdateInline?.(data);
  };

  const onInvalidatingChild = (child: T) => {
    if (!buildDataToSave) return;
    const data = buildDataToSave(child) as any;
    data.review_state = 0;
    onUpdateInline?.(data);
  };

  const sortedChildren = [...item.children].sort((a, b) =>
    ((a as any)[sortKey] ?? '').localeCompare((b as any)[sortKey] ?? '')
  );

  return (
    <td colSpan={columns.length} className="max-h-10 p-0">
      <table data-id={tableDataId} className="w-full table-fixed border-separate border-spacing-0">
        <colgroup>
          {columns.map((column, index) => (
            <col
              key={index}
              style={{
                width: column.width || `${100 / columns.length}%`,
                maxWidth: column.width || `${100 / columns.length}%`,
                minWidth: column.minWidth || 'auto',
              }}
            />
          ))}
        </colgroup>
        <tbody className="table-row-group">
          {sortedChildren.map(child => {
            const childId = child.id as string;

            return (
              <tr
                key={childId}
                className={clsx(
                  'group mb-size-12 table-row border-b border-neutral-30 transition first:mt-size-12 last:mb-0 hover:bg-neutral-10',
                  {
                    'outline outline-[3px] outline-offset-[-3px] outline-[var(--tw-shadow-color)]':
                      activeEntityId === childId,
                  }
                )}
              >
                <td className="py-size-12 pl-size-8 pr-size-2">
                  {childId && (
                    <div className="ml-1">
                      <Checkbox
                        checked={selectedPresencesIds.includes(childId)}
                        onChange={() => {
                          setSelectedPresencesIds(
                            selectedPresencesIds.includes(childId)
                              ? selectedPresencesIds.filter(id => id !== childId)
                              : [...selectedPresencesIds, childId]
                          );
                        }}
                      />
                    </div>
                  )}
                </td>
                <td className="py-size-12 pl-size-8 pr-size-2">{renderEntityCell(child)}</td>
                <td className="py-size-12 pl-size-8 pr-size-2">
                  <TimelineStartInlineControl
                    onPreview={onPreview}
                    item={child}
                    onUpdateInline={onUpdateInline}
                    buildDataToSave={buildDataToSave}
                    presenceData={presenceData}
                  />
                </td>
                <td className="py-size-12 pl-size-8 pr-size-2">
                  <TimelineEndInlineControl
                    onPreview={onPreview}
                    item={child}
                    onUpdateInline={onUpdateInline}
                    buildDataToSave={buildDataToSave}
                    presenceData={presenceData}
                  />
                </td>
                <td className="py-size-12 pl-size-8 pr-size-2">
                  <VisibilityInlineDropdown
                    onPreview={onPreview}
                    item={child}
                    onUpdateInline={onUpdateInline}
                    buildDataToSave={buildDataToSave}
                    presenceData={presenceData}
                  />
                </td>
                {showSupportColumn && (
                  <td className="py-size-12 pl-size-8 pr-size-2">
                    <SupportInlineDropdown
                      onPreview={onPreview as any}
                      item={child as any}
                      onUpdateInline={onUpdateInline as any}
                    />
                  </td>
                )}
                <td className="py-size-12 pl-size-8 pr-size-2">
                  {child.review_state === 1 ? (
                    <Chip className="my-[15px]" color={ChipColorEnum.ACTIVO}>
                      {t('Revisado')}
                    </Chip>
                  ) : (
                    <ReliabilityInlineDropdown
                      onPreview={onPreview}
                      item={child}
                      onUpdateInline={onUpdateInline}
                      buildDataToSave={buildDataToSave}
                      presenceData={presenceData}
                    />
                  )}
                </td>
                <td className="py-size-12 pl-size-8 pr-2">
                  <RenderActionButtons
                    alwaysVisible={true}
                    item={child}
                    onUpdate={() => onUpdate?.(child)}
                    onDelete={() => onDelete?.(child)}
                    onValidate={
                      buildDataToSave && !itemHasBeenValidated(child)
                        ? () => onValidatingChild(child)
                        : undefined
                    }
                    onInvalidate={
                      buildDataToSave && itemHasBeenValidated(child)
                        ? () => onInvalidatingChild(child)
                        : undefined
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </td>
  );
}
